import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  UtensilsCrossed,
  Users,
  Download,
  Lock,
  ArrowLeft,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider, isAdminEmail, ADMIN_EMAIL } from "@/lib/firebase";
import { subscribeAllRsvps, totalPlates, exportRsvpCsv, eventsLabel } from "@/lib/rsvpStore";
import { WEDDING } from "@/constants/testIds";

const GoogleGlyph = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.2 13.3 17.6 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-4 6.9-9.9 6.9-17.4z" />
    <path fill="#FBBC05" d="M10.4 28.3c-.5-1.5-.8-3-.8-4.8s.3-3.3.8-4.8l-7.8-6.1C1 15.7 0 19.7 0 23.5s1 7.8 2.6 11.1l7.8-6.3z" />
    <path fill="#34A853" d="M24 47c6.2 0 11.4-2 15.2-5.6l-7.3-5.7c-2 1.4-4.7 2.3-7.9 2.3-6.4 0-11.8-3.8-13.6-9.3l-7.8 6.3C6.5 42.6 14.6 47 24 47z" />
  </svg>
);

const Shell = ({ children }) => (
  <div className="grid min-h-screen place-items-center bg-[var(--green-deep)] px-6">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full max-w-sm rounded-3xl border border-gold/40 bg-white/[0.06] p-8 text-center backdrop-blur-md"
    >
      {children}
    </motion.div>
  </div>
);

const SignInGate = ({ onSignIn, busy }) => (
  <Shell>
    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[var(--gold)] text-[var(--green-deep)]">
      <Lock className="h-6 w-6" strokeWidth={1.8} />
    </span>
    <h1 className="mt-6 font-display text-3xl italic text-[var(--kasavu)]">
      Catering Dashboard
    </h1>
    <p className="mt-2 font-body text-sm text-kasavu/70">
      Private area — sign in with the host Google account to continue.
    </p>
    <button
      data-testid="admin-google-signin"
      onClick={onSignIn}
      disabled={busy}
      className="mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 font-body text-sm font-medium text-[var(--ink)] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleGlyph />}
      Sign in with Google
    </button>
    <a href="/" className="mt-5 inline-flex items-center gap-2 font-body text-xs text-kasavu/70 hover:text-[var(--gold)]">
      <ArrowLeft className="h-3 w-3" /> Back to invitation
    </a>
  </Shell>
);

const Denied = ({ email }) => (
  <Shell>
    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#ffb4a2] text-[var(--maroon)]">
      <ShieldAlert className="h-6 w-6" strokeWidth={1.8} />
    </span>
    <h1 className="mt-6 font-display text-3xl italic text-[var(--kasavu)]">Not authorised</h1>
    <p className="mt-2 font-body text-sm text-kasavu/70">
      <span className="text-[var(--gold)]">{email}</span> doesn't have access to this dashboard.
    </p>
    <button
      onClick={() => signOut(auth)}
      className="mt-6 w-full rounded-full bg-[var(--gold)] px-6 py-3.5 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--green-deep)]"
    >
      Sign out
    </button>
    <a href="/" className="mt-5 inline-flex items-center gap-2 font-body text-xs text-kasavu/70 hover:text-[var(--gold)]">
      <ArrowLeft className="h-3 w-3" /> Back to invitation
    </a>
  </Shell>
);

const Dashboard = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeAllRsvps(
      (list) => {
        setGuests(list);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        toast.error("Could not load guests", {
          description:
            err.code === "permission-denied"
              ? "Check Firestore security rules for admin read access."
              : err.message,
        });
      }
    );
    return unsub;
  }, []);

  const plates = totalPlates(guests);
  const parties = guests.filter((g) => g.accepted).length;

  const doExport = () => {
    exportRsvpCsv(guests);
    toast.success("Guest list exported", {
      description: "Saved to your downloads for the catering team.",
    });
  };

  return (
    <div className="min-h-screen bg-[var(--kasavu)] px-6 py-14 md:py-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <a href="/" className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.2em] text-[var(--maroon)] hover:opacity-80">
            <ArrowLeft className="h-4 w-4" /> Invitation
          </a>
          <button
            onClick={() => signOut(auth)}
            className="font-body text-xs uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--maroon)]"
          >
            Sign out
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-6 rounded-3xl border border-gold/40 bg-[var(--green-deep)] p-8 text-[var(--kasavu)] md:p-10"
        >
          <div className="flex items-center gap-2 text-[var(--gold)]">
            <UtensilsCrossed className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-body text-[10px] uppercase tracking-[0.26em]">
              Catering Dashboard · Live
            </span>
          </div>
          <h1 className="mt-3 font-display text-4xl italic">Guest List</h1>

          <div className="mt-6 flex gap-4">
            <Stat label="Parties" value={parties} />
            <Stat label="Plates" value={plates} icon />
          </div>

          <div className="mt-6 max-h-[52vh] space-y-2 overflow-y-auto pr-1">
            {loading && (
              <p className="flex items-center justify-center gap-2 rounded-xl bg-white/[0.06] px-4 py-6 font-body text-sm text-kasavu/60">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading responses…
              </p>
            )}
            {!loading && guests.length === 0 && (
              <p className="rounded-xl bg-white/[0.06] px-4 py-6 text-center font-body text-sm text-kasavu/60">
                No responses yet.
              </p>
            )}
            {guests.map((g) => (
              <div
                key={g.uid || g.email}
                data-testid={WEDDING.guestRow}
                className="flex items-center justify-between rounded-xl bg-white/[0.06] px-4 py-3"
              >
                <div>
                  <p className="font-body text-sm font-medium">{g.name}</p>
                  <p className="font-body text-[11px] text-kasavu/55">
                    {g.accepted ? "Attending" : "Not attending"} · {g.email}
                  </p>
                  {g.accepted && (
                    <p className="font-body text-[11px] text-[var(--gold)]">
                      {eventsLabel(g)}
                    </p>
                  )}
                </div>
                <span className="font-display text-2xl italic text-[var(--gold)]">
                  {g.accepted ? g.seats : "—"}
                </span>
              </div>
            ))}
          </div>

          <button
            data-testid={WEDDING.exportCsv}
            onClick={doExport}
            disabled={guests.length === 0}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] px-6 py-3.5 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--green-deep)] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Export CSV for catering
          </button>
        </motion.div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, icon }) => (
  <div className="flex-1 rounded-2xl border border-gold/30 bg-white/[0.05] px-5 py-4">
    <div className="flex items-center gap-2 text-[var(--gold)]">
      {icon && <Users className="h-4 w-4" strokeWidth={1.6} />}
      <span className="font-body text-[10px] uppercase tracking-[0.2em]">{label}</span>
    </div>
    <p className="mt-1 font-display text-4xl italic">{value}</p>
  </div>
);

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [busy, setBusy] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u)), []);

  const signIn = async () => {
    try {
      setBusy(true);
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      toast.error("Sign-in failed", {
        description:
          e.code === "auth/unauthorized-domain"
            ? "This domain isn't authorised in Firebase yet."
            : e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  if (user === undefined) {
    return (
      <Shell>
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-[var(--gold)]" />
      </Shell>
    );
  }
  if (!user) return <SignInGate onSignIn={signIn} busy={busy} />;
  if (!isAdminEmail(user.email)) return <Denied email={user.email} />;
  return <Dashboard />;
}
